/* eslint-disable @typescript-eslint/no-unused-vars */
// ============================================================
// SIMADCA v3.0 – CompliancePanel.tsx FINAL
// Conformitate directive UE – audit complet per parametru
// ============================================================

import React, { useState } from 'react';
import { Modal, Tag, Tooltip, Progress, Alert as AntAlert } from 'antd';
import {
  SafetyOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
  CloseCircleOutlined, InfoCircleOutlined, FilePdfOutlined,
  EnvironmentOutlined, ClockCircleOutlined, WarningOutlined,
} from '@ant-design/icons';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, Cell, ReferenceLine,
  PieChart, Pie, Legend,
} from 'recharts';

import { mockCompliance, mockFarms, mockWQI } from '../../data/mockData';
import { ComplianceItem } from '../../types';

// ─── Constante ────────────────────────────────────────────

const STATUS_COLOR = {
  compliant: '#10b981',
  warning:   '#f59e0b',
  exceeded:  '#ef4444',
};
const STATUS_BG = {
  compliant: '#d1fae5',
  warning:   '#fef3c7',
  exceeded:  '#fee2e2',
};
const STATUS_LABEL = {
  compliant: 'Conform',
  warning:   'Atenție',
  exceeded:  'Depășit',
};
const STATUS_ICON = {
  compliant: <CheckCircleOutlined />,
  warning:   <ExclamationCircleOutlined />,
  exceeded:  <CloseCircleOutlined />,
};

// ─── Directive UE explicații ──────────────────────────────

const DIRECTIVE_INFO: Record<string, { short: string; desc: string; year: string }> = {
  'Dir. 2013/39/UE – Substanțe prioritare': {
    short: 'Substanțe prioritare',
    desc:  'Stabilește standarde de calitate ecologică pentru substanțe prioritare în apele de suprafață, inclusiv antibiotice.',
    year:  '2013',
  },
  'Dir. 2013/39/UE Art.16 – Perturbatori endocrini': {
    short: 'Perturbatori endocrini',
    desc:  'Reglementează estrogenii sintetici și naturali ca perturbatori endocrini cu impact asupra ecosistemelor acvatice.',
    year:  '2013',
  },
  'Dir. 91/676/CEE – Protecție ape nitrați': {
    short: 'Nitrați',
    desc:  'Directiva nitrați – protecția apelor împotriva poluării cauzate de nitrați din surse agricole. Limită: 50 mg/L.',
    year:  '1991',
  },
  'Reg. 1107/2009/CE – Pesticide': {
    short: 'Pesticide',
    desc:  'Reglementarea plasării pe piață a produselor fitosanitare, inclusiv gestionarea reziduurilor în ape.',
    year:  '2009',
  },
  'Dir. 2000/60/CE Art.16 – Cadrul apei': {
    short: 'Cadrul apei',
    desc:  'Directiva Cadru Apă – stabilește un cadru pentru acțiunea comunitară în domeniul politicii apei.',
    year:  '2000',
  },
};

// ─── Row conformitate ─────────────────────────────────────

const ComplianceRow: React.FC<{ item: ComplianceItem; onClick: () => void }> = ({ item, onClick }) => {
  const pct         = Math.round((item.currentValue / item.limit) * 100);
  const color       = STATUS_COLOR[item.status];
  const dirInfo     = DIRECTIVE_INFO[item.directive];

  return (
    <div
      onClick={onClick}
      style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr 1.2fr',
        gap: 12, padding: '14px 16px',
        alignItems: 'center', cursor: 'pointer',
        borderBottom: '1px solid #f8fafc',
        transition: 'background 0.15s',
      }}
      onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.background = '#fafbff'}
      onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.background = 'transparent'}
    >
      {/* Parametru */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{item.parameter}</div>
        <Tooltip title={dirInfo?.desc}>
          <div style={{ fontSize: 10, color: '#94a3b8', cursor: 'help', display: 'flex', alignItems: 'center', gap: 3 }}>
            {dirInfo?.short ?? item.directive} <InfoCircleOutlined style={{ fontSize: 9 }} />
          </div>
        </Tooltip>
      </div>

      {/* Bar pct */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color }}>{item.currentValue} {item.unit}</span>
          <span style={{ fontSize: 10, color: '#94a3b8' }}>limită: {item.limit}</span>
        </div>
        <div style={{ height: 6, borderRadius: 99, background: '#f1f5f9', overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 99,
            width: `${Math.min(pct, 100)}%`,
            background: color,
            transition: 'width 0.6s ease',
          }} />
        </div>
        {item.exceedancePct && (
          <div style={{ fontSize: 9, color: '#ef4444', marginTop: 2 }}>+{item.exceedancePct.toFixed(1)}% față de limită</div>
        )}
      </div>

      {/* % limită */}
      <div style={{ textAlign: 'center' }}>
        <span style={{ fontSize: 14, fontWeight: 800, color }}>{pct}%</span>
      </div>

      {/* Limita */}
      <div style={{ textAlign: 'center', fontSize: 12, color: '#475569' }}>
        {item.limit} {item.unit}
      </div>

      {/* Senzori */}
      <div style={{ fontSize: 11, color: '#64748b' }}>
        {item.sensors.join(', ')}
      </div>

      {/* Status badge */}
      <div>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 99,
          background: STATUS_BG[item.status], color,
        }}>
          {STATUS_ICON[item.status]} {STATUS_LABEL[item.status]}
        </span>
      </div>
    </div>
  );
};

// ─── Modal detaliu item ───────────────────────────────────

const DetailModal: React.FC<{ item: ComplianceItem | null; onClose: () => void }> = ({ item, onClose }) => {
  if (!item) return null;
  const pct      = Math.round((item.currentValue / item.limit) * 100);
  const color    = STATUS_COLOR[item.status];
  const dirInfo  = DIRECTIVE_INFO[item.directive];
  const farms    = item.sensors.map(sId => {
    const farm = mockFarms.find(f => f.sensorIds.includes(sId));
    return farm?.name ?? sId;
  });

  return (
    <Modal
      open={!!item} onCancel={onClose} footer={null}
      width={540} title={null}
      styles={{ body: { padding: 0 } }}
    >
      <div style={{ padding: '24px 28px', background: `${color}08`, borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: STATUS_BG[item.status], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, color }}>
            {STATUS_ICON[item.status]}
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{item.parameter}</h3>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: STATUS_BG[item.status], color }}>
              {STATUS_LABEL[item.status]}
            </span>
          </div>
        </div>
      </div>

      <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Valori */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          {[
            { label: 'Valoare curentă', value: `${item.currentValue} ${item.unit}`, color },
            { label: 'Limită legală UE', value: `${item.limit} ${item.unit}`, color: '#64748b' },
            { label: '% din limită', value: `${pct}%`, color: pct > 100 ? '#ef4444' : pct > 80 ? '#f59e0b' : '#10b981' },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: 'center', padding: '12px', borderRadius: 10, background: '#f8fafc', border: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Directivă */}
        <div style={{ padding: '14px', borderRadius: 10, background: '#f0f9ff', border: '1px solid #bae6fd' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#0369a1', marginBottom: 4 }}>
            📋 {item.directive}
          </div>
          <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.5 }}>
            {dirInfo?.desc ?? 'Directivă europeană de mediu pentru protecția apelor.'}
          </div>
          {dirInfo?.year && (
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 6 }}>Adoptată în {dirInfo.year} · Transpusă în legislația națională</div>
          )}
        </div>

        {/* Senzori & ferme */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Senzori implicați</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {item.sensors.map(s => (
              <span key={s} style={{ fontSize: 11, padding: '3px 9px', borderRadius: 99, background: '#eef2ff', color: '#6366f1', fontWeight: 600 }}>
                {s}
              </span>
            ))}
          </div>
          {farms.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {Array.from(new Set(farms)).map(f => (
                <span key={f} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 99, background: '#f8fafc', color: '#64748b', border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <EnvironmentOutlined style={{ fontSize: 9 }} /> {f}
                </span>
              ))}
            </div>
          )}
        </div>

        {item.status === 'exceeded' && (
          <AntAlert
            type="error" showIcon
            message="Acțiune necesară"
            description="Depășirea limitei legale impune notificarea autorităților de mediu (APM/Garda de Mediu) în maxim 24h conform Legii 265/2006."
            style={{ borderRadius: 10 }}
          />
        )}
        {item.status === 'warning' && (
          <AntAlert
            type="warning" showIcon
            message="Monitorizare intensificată"
            description="Valoarea apropiată de limita legală necesită măsurători suplimentare și pregătirea unui plan de intervenție."
            style={{ borderRadius: 10 }}
          />
        )}
      </div>
    </Modal>
  );
};

// ─── Panel principal ──────────────────────────────────────

const CompliancePanel: React.FC = () => {
  const [selected, setSelected] = useState<ComplianceItem | null>(null);
  const [filter,   setFilter]   = useState<'all' | 'compliant' | 'warning' | 'exceeded'>('all');

  const { totalChecked, compliant, warning, exceeded, lastAudit, items } = mockCompliance;

  const filtered = filter === 'all' ? items : items.filter(i => i.status === filter);

  const pieData = [
    { name: 'Conforme',    value: compliant, color: '#10b981' },
    { name: 'Atenție',     value: warning,   color: '#f59e0b' },
    { name: 'Depășite',    value: exceeded,  color: '#ef4444' },
  ];

  const barData = items.map(i => ({
    name:   i.parameter.length > 14 ? i.parameter.slice(0, 14) + '…' : i.parameter,
    pct:    Math.round((i.currentValue / i.limit) * 100),
    color:  STATUS_COLOR[i.status],
    status: i.status,
  }));

  const complianceRate = Math.round((compliant / totalChecked) * 100);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>
            Conformitate UE
          </h2>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>
            Audit față de directive europene apă · Ultimul audit: {new Date(lastAudit).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 10, background: complianceRate >= 80 ? '#d1fae5' : complianceRate >= 60 ? '#fef3c7' : '#fee2e2', border: `1px solid ${complianceRate >= 80 ? '#a7f3d0' : complianceRate >= 60 ? '#fde68a' : '#fecaca'}` }}>
          <SafetyOutlined style={{ color: complianceRate >= 80 ? '#10b981' : complianceRate >= 60 ? '#f59e0b' : '#ef4444', fontSize: 18 }} />
          <div>
            <div style={{ fontSize: 20, fontWeight: 900, color: complianceRate >= 80 ? '#10b981' : complianceRate >= 60 ? '#f59e0b' : '#ef4444' }}>{complianceRate}%</div>
            <div style={{ fontSize: 10, color: '#64748b' }}>Rată conformitate</div>
          </div>
        </div>
      </div>

      {/* KPI + Pie */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
        {/* KPI cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {[
            { label: 'Conforme',  value: compliant, total: totalChecked, color: '#10b981', bg: '#d1fae5', icon: <CheckCircleOutlined /> },
            { label: 'Atenție',   value: warning,   total: totalChecked, color: '#f59e0b', bg: '#fef3c7', icon: <ExclamationCircleOutlined /> },
            { label: 'Depășite',  value: exceeded,  total: totalChecked, color: '#ef4444', bg: '#fee2e2', icon: <CloseCircleOutlined /> },
          ].map((k, i) => (
            <div
              key={i}
              onClick={() => setFilter(filter === ['compliant','warning','exceeded'][i] as any ? 'all' : ['compliant','warning','exceeded'][i] as any)}
              style={{ background: '#fff', borderRadius: 14, border: `2px solid ${filter === ['compliant','warning','exceeded'][i] ? k.color : '#e2e8f0'}`, padding: '16px', cursor: 'pointer', transition: 'all 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
            >
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: k.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: k.color }}>
                  {k.icon}
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>{k.label}</span>
              </div>
              <div style={{ fontSize: 28, fontWeight: 900, color: k.color }}>{k.value}</div>
              <div style={{ fontSize: 10, color: '#94a3b8' }}>din {k.total} verificate</div>
            </div>
          ))}
        </div>

        {/* Pie chart */}
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Distribuție conformitate</div>
          <ResponsiveContainer width="100%" height={140}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={35} outerRadius={55} paddingAngle={3} dataKey="value">
                {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Legend iconSize={10} wrapperStyle={{ fontSize: 10 }} />
              <RTooltip contentStyle={{ borderRadius: 8, fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bar chart % față de limită */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Valori curente ca % din limita legală</div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 14 }}>Linie roșie = 100% (limita legală). Barele roșii = depășire activă.</div>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={barData} margin={{ top: 4, right: 16, bottom: 0, left: -16 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
            <YAxis domain={[0, Math.max(200, Math.max(...barData.map(b => b.pct)) + 20)]} tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} />
            <RTooltip contentStyle={{ borderRadius: 10, fontSize: 11 }} formatter={(v: any) => [`${v}% din limită`]} />
            <ReferenceLine y={100} stroke="#ef4444" strokeDasharray="6 3" strokeWidth={2} />
            <Bar dataKey="pct" radius={[4, 4, 0, 0]}>
              {barData.map((b, i) => <Cell key={i} fill={b.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Tabel conformitate */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #f1f5f9', padding: '0 16px' }}>
          {[
            { key: 'all',       label: `Toate (${items.length})` },
            { key: 'exceeded',  label: `🔴 Depășite (${exceeded})` },
            { key: 'warning',   label: `🟡 Atenție (${warning})` },
            { key: 'compliant', label: `✅ Conforme (${compliant})` },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key as any)}
              style={{
                padding: '12px 16px', fontSize: 12,
                fontWeight: filter === t.key ? 700 : 500,
                color: filter === t.key ? '#6366f1' : '#64748b',
                border: 'none', background: 'none', cursor: 'pointer',
                borderBottom: `2px solid ${filter === t.key ? '#6366f1' : 'transparent'}`,
                marginBottom: -1, fontFamily: 'inherit', transition: 'all 0.15s',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Header tabel */}
        <div style={{
          display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr 1.2fr',
          gap: 12, padding: '10px 16px',
          background: '#f8fafc', fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em',
        }}>
          {['PARAMETRU / DIRECTIVĂ', 'VALOARE CURENTĂ', '% LIMITĂ', 'LIMITĂ LEGALĂ', 'SENZORI', 'STATUS'].map(h => (
            <div key={h}>{h}</div>
          ))}
        </div>

        {filtered.map((item, i) => (
          <ComplianceRow key={i} item={item} onClick={() => setSelected(item)} />
        ))}

        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: 13 }}>
            <SafetyOutlined style={{ fontSize: 24, display: 'block', marginBottom: 8 }} />
            Nicio înregistrare în categoria selectată
          </div>
        )}
      </div>

      {/* Legal disclaimer */}
      <div style={{ background: '#f0f9ff', borderRadius: 14, border: '1px solid #bae6fd', padding: '14px 18px' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <InfoCircleOutlined style={{ color: '#0369a1', fontSize: 16, marginTop: 2 }} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#0369a1', marginBottom: 4 }}>Cadru legal aplicabil</div>
            <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.6 }}>
              Monitorizarea se realizează conform: Directiva 2000/60/CE (Cadrul Apei) · Directiva 2013/39/UE (Substanțe prioritare) ·
              Directiva 91/676/CEE (Nitrați) · Regulamentul 1107/2009/CE (Pesticide) · Legea 107/1996 (Legea apelor) ·
              Legea 265/2006 (Protecția mediului). Depășirile trebuie raportate la APM și Garda Națională de Mediu.
            </div>
          </div>
        </div>
      </div>

      <DetailModal item={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default CompliancePanel;